import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreateProviderInput,
  Provider,
  ProvidersListResponse,
  UpdateProviderInput,
} from '@/features/crm/types/provider.types';
import { apiFetch } from '@/lib/api';

/**
 * CRM providers — TanStack Query hooks.
 *
 * Pattern matches `apps/web/src/features/catalog/api/brands.ts`.
 * Providers have one wire field less than clients (no
 * `assignedSellerId`) and the DB column shape is correspondingly
 * flatter — the response shape is straight from `ProviderResource`.
 */

export type ProvidersSortKey = 'name' | 'identificationNumber';
export type SortDirection = 'asc' | 'desc';

export interface ProvidersListParams {
  page: number;
  pageSize: number;
  sort?: ProvidersSortKey;
  dir?: SortDirection;
  search?: string;
  isActive?: 'active' | 'inactive' | 'all';
}

function buildQuery(params: ProvidersListParams): string {
  const sp = new URLSearchParams();
  sp.set('page', String(params.page));
  sp.set('pageSize', String(params.pageSize));
  if (params.sort) sp.set('sort', params.sort);
  if (params.dir) sp.set('dir', params.dir);
  if (params.search) sp.set('search', params.search);
  if (params.isActive === 'inactive') sp.set('isActive', 'false');
  if (params.isActive === 'all') sp.set('includeInactive', 'true');
  return sp.toString();
}

export const providersKeys = {
  all: ['providers'] as const,
  list: (params: ProvidersListParams) => ['providers', 'list', params] as const,
  detail: (id: string) => ['providers', 'detail', id] as const,
};

export function useProviders(params: ProvidersListParams) {
  return useQuery({
    queryKey: providersKeys.list(params),
    queryFn: () => apiFetch<ProvidersListResponse>(`/providers?${buildQuery(params)}`),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

export function useProvider(id: string | null) {
  return useQuery({
    queryKey: providersKeys.detail(id ?? ''),
    queryFn: () => apiFetch<Provider>(`/providers/${id}`),
    enabled: Boolean(id),
    staleTime: 30_000,
  });
}

export function useCreateProvider() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateProviderInput) =>
      apiFetch<Provider>('/providers', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: providersKeys.all }),
  });
}

export function useUpdateProvider() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateProviderInput }) =>
      apiFetch<Provider>(`/providers/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(input),
      }),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: providersKeys.all });
      qc.invalidateQueries({ queryKey: providersKeys.detail(id) });
    },
  });
}

export function useDeleteProvider() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<{ ok: boolean; id: string }>(`/providers/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: providersKeys.all }),
  });
}
